import React from "react";
import { mutate } from "swr";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/app/DatePicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus } from "@phosphor-icons/react";
import { fmtN, todayISO } from "./common";

const EMPTY = { name: "", account_code: "", acquisition_date: todayISO(), amount: "", duration_years: "5" };

export function AssetFormDialog({ file, immoAccounts, listKey }) {
  const [open, setOpen] = React.useState(false);
  const [form, setForm] = React.useState(EMPTY);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/accounting/files/${file.id}/assets`, {
        ...form, amount: parseFloat(form.amount), duration_years: parseInt(form.duration_years, 10),
      });
      toast.success("Immobilisation enregistrée — acquisition comptabilisée (OD)");
      setOpen(false);
      setForm({ ...EMPTY, acquisition_date: todayISO() });
      mutate(listKey);
    } catch (err) { toast.error(err?.response?.data?.detail || "Enregistrement impossible"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-[#2E3192] hover:bg-[#1E2066]" data-testid="new-asset-btn"><Plus size={16} className="mr-1" /> Nouvelle immobilisation</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl">Nouvelle immobilisation</DialogTitle>
          <DialogDescription>Comptes classes 20 à 26 (hors amortissements 28x et dépréciations 29x).</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label>Désignation *</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="asset-name" placeholder="Ordinateur portable…" /></div>
          <div>
            <Label>Compte d'immobilisation *</Label>
            <Input required list="immo-accounts" value={form.account_code} onChange={(e) => setForm({ ...form, account_code: e.target.value })} data-testid="asset-account" placeholder="2446" />
            <datalist id="immo-accounts">{immoAccounts.map((a) => (<option key={a.code} value={a.code}>{a.name}</option>))}</datalist>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Date d'acquisition</Label><DatePicker value={form.acquisition_date} onChange={(v) => setForm({ ...form, acquisition_date: v })} testid="asset-acquisition-date" /></div>
            <div><Label>Valeur *</Label><Input required type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="asset-amount" /></div>
            <div><Label>Durée (ans)</Label><Input type="number" min="1" max="30" value={form.duration_years} onChange={(e) => setForm({ ...form, duration_years: e.target.value })} /></div>
          </div>
          {form.amount && form.duration_years && (
            <div className="text-sm text-zinc-600">Annuité linéaire : <span className="font-medium text-zinc-900">{fmtN(parseFloat(form.amount) / parseInt(form.duration_years || "5", 10))}</span> {file.currency}/an</div>
          )}
          <div className="flex justify-end"><Button type="submit" disabled={!form.name || !form.account_code || !form.amount} className="bg-[#2E3192] hover:bg-[#1E2066]" data-testid="asset-submit">Enregistrer</Button></div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function AssetsTable({ assets, currentYear, onDepreciate }) {
  return (
    <div className="border border-zinc-200 bg-white rounded-md overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-zinc-50 border-b border-zinc-200">
          <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500">
            <th className="px-4 py-2">Désignation</th><th className="px-4 py-2">Compte</th><th className="px-4 py-2">Acquisition</th>
            <th className="px-4 py-2 text-right">Valeur</th><th className="px-4 py-2 text-right">Annuité</th>
            <th className="px-4 py-2 text-right">Amort. cumulés</th><th className="px-4 py-2 text-right">VNC</th><th className="px-4 py-2"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-100">
          {assets.length === 0 && <tr><td colSpan={8} className="p-6 text-zinc-500">Aucune immobilisation.</td></tr>}
          {assets.map((a) => {
            const doneCurrentYear = a.depreciated_years.includes(currentYear);
            const fullyDepreciated = a.depreciated_years.length >= a.duration_years;
            return (
              <tr key={a.id} data-testid={`asset-row-${a.id}`}>
                <td className="px-4 py-2 font-medium">{a.name}</td>
                <td className="px-4 py-2 font-mono text-xs">{a.account_code}</td>
                <td className="px-4 py-2">{a.acquisition_date}</td>
                <td className="px-4 py-2 text-right">{fmtN(a.amount)}</td>
                <td className="px-4 py-2 text-right">{fmtN(a.annuite)}</td>
                <td className="px-4 py-2 text-right">{fmtN(a.amortissements_cumules)}</td>
                <td className="px-4 py-2 text-right font-medium">{fmtN(a.vnc)}</td>
                <td className="px-4 py-2 text-right">
                  {!doneCurrentYear && !fullyDepreciated && (
                    <button onClick={() => onDepreciate(a)} className="text-xs inline-flex items-center gap-1 text-zinc-600 hover:text-zinc-900 border border-zinc-200 rounded px-2 py-1" title={`Comptabiliser la dotation ${currentYear}`} data-testid={`asset-depreciate-${a.id}`}>
                      Dotation {currentYear}
                    </button>
                  )}
                  {doneCurrentYear && <span className="text-[10px] text-zinc-400">Dotation {currentYear} passée</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
